import React, { useContext, useMemo, useState } from "react";
import { Card, Col, Empty, Row, Spin, Tabs } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { DeploymentUnitOutlined, HomeOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { AppContext } from "../../../context/AppContextProvider";

import AddonCard from "./AddonCard";
import AddonPreviewModal from "./AddonPreviewModal";
import useAddonReload from "./useAddonReload";
import useAddons from "./useAddons";
import { isAddonFree } from "./addonPresentation";

const Addons = () => {
  const { appConfig, authUser } = useContext(AppContext);
  const { t } = useTranslation();

  const [activeFilter, setActiveFilter] = useState("all");
  const [previewAddonId, setPreviewAddonId] = useState(null);

  const isRtl = appConfig?.direction === "rtl";
  const activeAddons = authUser?.active_addons || [];

  const {
    reloadData,
    actionLoading,
    setActionLoading,
    pendingActionReloadRef,
    handleReload,
  } = useAddonReload();

  const { loading: isDataLoading, addonData } = useAddons({
    reloadData,
    pendingActionReloadRef,
    setActionLoading,
  });

  const filters = useMemo(
    () => [
      { key: "all", label: t("addon_filter_all_modules") },
      { key: "active", label: t("active") },
      { key: "available", label: t("addon_available") },
      { key: "free", label: t("free") },
      { key: "paid", label: t("paid") },
    ],
    [t],
  );

  const filteredAddons = useMemo(() => {
    switch (activeFilter) {
      case "active":
        return addonData.filter((a) => a?.is_subscribed);
      case "available":
        return addonData.filter((a) => !a?.is_subscribed);
      case "free":
        return addonData.filter(isAddonFree);
      case "paid":
        return addonData.filter((a) => !isAddonFree(a));
      default:
        return addonData;
    }
  }, [activeFilter, addonData]);

  const previewAddon = useMemo(
    () => addonData.find((addon) => addon?.id === previewAddonId) || null,
    [addonData, previewAddonId],
  );

  const breadcrumbRoutes = [
    { path: "/", breadcrumbName: t("home"), icon: <HomeOutlined /> },
    {
      path: "addons",
      breadcrumbName: t("addons"),
      icon: <DeploymentUnitOutlined />,
    },
  ];

  return (
    <div className="addons-showcase-page" dir={isRtl ? "rtl" : "ltr"}>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          title={t("addons")}
          ghost={false}
          className="app-p-0"
        />
      </Card>

      <div className="addons-showcase">
        <Tabs
          activeKey={activeFilter}
          onChange={setActiveFilter}
          className="addons-showcase__filters app-pill-tabs"
          items={filters}
        />

        <Spin spinning={isDataLoading || actionLoading}>
          {filteredAddons.length > 0 ? (
            <Row gutter={[18, 18]}>
              {filteredAddons.map((addon) => (
                <Col xs={24} sm={24} md={12} lg={8} xl={8} key={addon?.id}>
                  <AddonCard
                    addon={addon}
                    isRtl={isRtl}
                    activeAddons={activeAddons}
                    t={t}
                    onOpenPreview={setPreviewAddonId}
                    onReload={handleReload}
                    onActionLoadingChange={setActionLoading}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <Empty
              className="addon-showcase-empty"
              description={t("no_addons_found")}
            />
          )}
        </Spin>
      </div>

      <AddonPreviewModal
        addon={previewAddon}
        open={Boolean(previewAddon)}
        onClose={() => setPreviewAddonId(null)}
        reloadCallback={handleReload}
        actionLoading={actionLoading}
        onActionLoadingChange={setActionLoading}
      />
    </div>
  );
};

export default Addons;
